import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, GitCompare } from "lucide-react";
import {
  compareCrossBranchSnapshots,
  compareRepoSnapshots,
  deleteRepo,
  getRepo,
  listRepoSnapshots,
} from "./api";
import SourceCompareAddSource from "./SourceCompareAddSource";
import ProgressBar from "./ProgressBar";
import {
  analysisHealthCopy,
  analysisProgressFromRepo,
  deriveAnalysisStage,
  formatAnalysisProgressCopy,
  formatAnalysisStageLabel,
} from "./analysisProgress";
import {
  branchLabelFromRepo,
  buildComparisonSections,
  buildSourceCompareInterpretation,
  buildSourceCompareItems,
  effectiveSourceStatus,
  formatComparisonHeadline,
  formatSnapshotSourceLabel,
  formatSourceMetadataLines,
  formatSourceStatus,
  hasLegacySnapshotsWithoutSource,
  pickDefaultBaseHeadSnapshotIds,
} from "./sourceCompareDisplay";
import {
  emptySourceCompareSession,
  loadSourceCompareSession,
  saveSourceCompareSession,
} from "./sourceCompareSession";
import "./SourceComparePanel.css";
import "./UserDashboard.css";

function getErrorMessage(err, fallback) {
  if (err?.message) return err.message;
  if (typeof err === "string") return err;
  return fallback;
}

function deduplicateSnapshotsByBranch(snapshots) {
  const byBranch = new Map();
  for (const s of snapshots) {
    const src = s.source ?? {};
    const key = src.branch || src.ref || src.defaultBranch || s.id;
    const existing = byBranch.get(key);
    if (!existing) {
      byBranch.set(key, s);
    } else {
      const existingTime = new Date(existing.generatedAt ?? existing.createdAt ?? 0).getTime();
      const newTime = new Date(s.generatedAt ?? s.createdAt ?? 0).getTime();
      if (newTime > existingTime) {
        byBranch.set(key, s);
      }
    }
  }
  return Array.from(byBranch.values());
}

const EMPTY_BRANCH_ANALYSES = [];

const SourceComparePanel = ({
  repo,
  branchAnalyses = EMPTY_BRANCH_ANALYSES,
  canonicalRepoId,
  canAddSource = true,
  onOpenNewRepository,
  onAnalysesRefresh,
  onOpenSource,
  onSourcesChange,
}) => {
  const repoId = repo?._id;
  const sessionRepoId = canonicalRepoId || repoId;
  const initialSession = loadSourceCompareSession(sessionRepoId) || emptySourceCompareSession();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState("");
  const [baseSnapshotId, setBaseSnapshotId] = useState(initialSession.selection?.baseSnapshotId || "");
  const [headSnapshotId, setHeadSnapshotId] = useState(initialSession.selection?.headSnapshotId || "");
  const [compareResult, setCompareResult] = useState(initialSession.compareResult || null);
  const [preferredSelection, setPreferredSelection] = useState(initialSession.preferredSelection || null);
  const [addSourceState, setAddSourceState] = useState(initialSession.addSource || null);
  const [liveByRepoId, setLiveByRepoId] = useState({});

  const snapshotRepoIdMap = useMemo(() => {
    const map = {};
    for (const s of snapshots) {
      if (s.repoId) map[s.id] = s.repoId;
    }
    return map;
  }, [snapshots]);

  const loadSnapshots = useCallback(async () => {
    if (!repoId) return;
    setLoading(true);
    setError("");
    try {
      const branchRepoIds = branchAnalyses
        .filter((b) => b.status === "completed" || b.status === "processing" || b.status === "pending")
        .map((b) => b.repoId);
      const allRepoIds = [repoId, ...branchRepoIds.filter((id) => id !== repoId)];
      const uniqueIds = [...new Set(allRepoIds)];

      const results = await Promise.all(
        uniqueIds.map((id) =>
          listRepoSnapshots(id, { limit: 50 }).catch(() => ({ snapshots: [] }))
        )
      );
      const allSnapshots = results.flatMap((r) => r?.snapshots ?? []);
      const deduped = deduplicateSnapshotsByBranch(allSnapshots);
      setSnapshots(deduped);
      const defaults = pickDefaultBaseHeadSnapshotIds(allSnapshots);
      if (preferredSelection?.headSha || preferredSelection?.headRepoId) {
        const head = deduped.find(
          (s) =>
            (preferredSelection.headSha &&
              s.source?.commitSha === preferredSelection.headSha) ||
            (preferredSelection.headRepoId &&
              String(s.repoId) === String(preferredSelection.headRepoId))
        );
        const base = deduped.find(
          (s) =>
            String(s.repoId) === String(preferredSelection.baseRepoId || repoId) &&
            s.id !== head?.id
        ) || deduped.find((s) => s.id !== head?.id);
        if (head) setHeadSnapshotId(head.id);
        if (base) setBaseSnapshotId(base.id);
      } else {
        setBaseSnapshotId((current) =>
          current && deduped.some((snapshot) => snapshot.id === current)
            ? current
            : defaults.baseSnapshotId
        );
        setHeadSnapshotId((current) =>
          current && deduped.some((snapshot) => snapshot.id === current)
            ? current
            : defaults.headSnapshotId
        );
      }
    } catch (err) {
      setSnapshots([]);
      setError(getErrorMessage(err, "Failed to load snapshots."));
    } finally {
      setLoading(false);
    }
  }, [repoId, branchAnalyses, preferredSelection]);

  useEffect(() => {
    loadSnapshots();
  }, [loadSnapshots]);

  useEffect(() => {
    if (!sessionRepoId) return;
    saveSourceCompareSession(sessionRepoId, {
      addSource: addSourceState,
      selection: { baseSnapshotId, headSnapshotId },
      preferredSelection,
      compareResult,
    });
  }, [sessionRepoId, addSourceState, baseSnapshotId, headSnapshotId, preferredSelection, compareResult]);

  const legacyWarning = useMemo(
    () => hasLegacySnapshotsWithoutSource(snapshots),
    [snapshots]
  );

  const canCompare =
    Boolean(baseSnapshotId) &&
    Boolean(headSnapshotId) &&
    baseSnapshotId !== headSnapshotId &&
    !comparing;

  const handleSourceReady = async ({ repoId: newRepoId, commitSha }) => {
    setError("");
    if (onAnalysesRefresh) {
      await onAnalysesRefresh();
    }
    setPreferredSelection({
      baseRepoId: canonicalRepoId || repoId,
      headRepoId: newRepoId,
      headSha: commitSha,
    });
  };

  const handleCompare = async () => {
    if (!canCompare || !repoId) return;
    setComparing(true);
    setError("");
    setCompareResult(null);
    try {
      const beforeRepo = snapshotRepoIdMap[baseSnapshotId] || repoId;
      const afterRepo = snapshotRepoIdMap[headSnapshotId] || repoId;
      const isCrossBranch = beforeRepo !== afterRepo;

      let result;
      if (isCrossBranch) {
        result = await compareCrossBranchSnapshots({
          canonicalRepoId: canonicalRepoId || repoId,
          beforeRepoId: beforeRepo,
          beforeSnapshotId: baseSnapshotId,
          afterRepoId: afterRepo,
          afterSnapshotId: headSnapshotId,
        });
      } else {
        result = await compareRepoSnapshots({
          repoId: beforeRepo,
          beforeSnapshotId: baseSnapshotId,
          afterSnapshotId: headSnapshotId,
        });
      }
      setCompareResult(result);
    } catch (err) {
      setError(getErrorMessage(err, "Source Compare failed."));
    } finally {
      setComparing(false);
    }
  };

  const comparisonSections = useMemo(
    () => buildComparisonSections(compareResult?.comparison),
    [compareResult]
  );

  const interpretation = useMemo(() => {
    if (!compareResult) return null;
    return buildSourceCompareInterpretation(
      compareResult.comparison,
      compareResult.beforeSnapshot,
      compareResult.afterSnapshot
    );
  }, [compareResult]);

  const headline = useMemo(() => {
    if (!compareResult) return null;
    return formatComparisonHeadline(
      compareResult.beforeSnapshot,
      compareResult.afterSnapshot,
      compareResult.comparison
    );
  }, [compareResult]);

  const persistAddSource = useCallback((next) => {
    setAddSourceState(next);
  }, []);

  const handleDeleteFailedSource = async (source) => {
    if (!source?.repoId || String(source.repoId) === String(canonicalRepoId || repoId)) {
      return;
    }
    const confirmed = window.confirm(
      `Delete the failed analysis for ${source.title || "this branch"}? Credits for that run are returned if they were charged.`
    );
    if (!confirmed) return;
    try {
      await deleteRepo(source.repoId);
      setLiveByRepoId((current) => {
        const next = { ...current };
        delete next[String(source.repoId)];
        return next;
      });
      await loadSnapshots();
      await onAnalysesRefresh?.();
    } catch (err) {
      setError(getErrorMessage(err, "Could not delete that failed analysis."));
    }
  };

  const handleJobStarted = async (started) => {
    setPreferredSelection({
      baseRepoId: canonicalRepoId || repoId,
      headRepoId: started?.repoId,
      headSha: started?.preflight?.headPin?.sha || started?.commitSha,
    });
    if (onAnalysesRefresh) {
      await onAnalysesRefresh();
    }
  };

  const sourceItems = useMemo(
    () =>
      buildSourceCompareItems({
        snapshots,
        branchAnalyses,
        canonical: repo
          ? {
              repoId: repo._id,
              title: branchLabelFromRepo(repo),
              status: repo.status,
              commitSha: repo.analysisSource?.commitSha,
              detail: repo.analysisSource?.commitSha
                ? `${String(repo.analysisSource.commitSha).slice(0, 7)} · ${
                    repo.processedFiles?.length || repo.processableFiles || 0
                  } files`
                : formatSourceStatus(repo.status),
            }
          : null,
        pending: addSourceState?.job
          ? {
              repoId: addSourceState.job.repoId,
              sourceInput: addSourceState.sourceInput,
              phase: addSourceState.phase,
              preflight: addSourceState.preflight,
            }
          : null,
      }),
    [snapshots, branchAnalyses, addSourceState, repo]
  );

  useEffect(() => {
    onSourcesChange?.(
      sourceItems
        .filter((item) => item.repoId)
        .map((item) => ({
          repoId: item.repoId,
          branch: item.title,
          title: item.title,
          status: item.status,
          commitSha: item.commitSha,
        }))
    );
  }, [sourceItems, onSourcesChange]);

  const analyzingRepoIds = useMemo(
    () =>
      sourceItems
        .filter((item) => {
          if (!item.repoId) return false;
          const live = liveByRepoId[String(item.repoId)];
          if (
            live?.status === "completed" ||
            live?.status === "failed" ||
            live?.finalizeStatus === "failed"
          ) {
            return false;
          }
          return (
            item.status === "processing" ||
            item.status === "queued" ||
            item.status === "pending"
          );
        })
        .map((item) => String(item.repoId)),
    [sourceItems, liveByRepoId]
  );
  const analyzingKey = analyzingRepoIds.join("|");
  const loadSnapshotsRef = useRef(loadSnapshots);
  const refreshAnalysesRef = useRef(onAnalysesRefresh);
  loadSnapshotsRef.current = loadSnapshots;
  refreshAnalysesRef.current = onAnalysesRefresh;

  useEffect(() => {
    if (!analyzingKey) return undefined;
    const ids = analyzingKey.split("|").filter(Boolean);
    let cancelled = false;
    const poll = async () => {
      const next = {};
      let completed = false;
      await Promise.all(
        ids.map(async (id) => {
          try {
            const live = await getRepo(id);
            next[id] = live;
            if (
              live?.status === "completed" ||
              live?.status === "failed" ||
              live?.finalizeStatus === "failed"
            ) {
              completed = true;
            }
          } catch {
            next[id] = null;
          }
        })
      );
      if (cancelled) return;
      setLiveByRepoId((current) => ({ ...current, ...next }));
      if (completed) {
        await loadSnapshotsRef.current?.();
        await refreshAnalysesRef.current?.();
      }
    };
    poll();
    const timer = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [analyzingKey]);

  const addSource = (
    <SourceCompareAddSource
      repoId={canonicalRepoId || repoId}
      canAddSource={canAddSource}
      onOpenNewRepository={onOpenNewRepository}
      onSourceReady={handleSourceReady}
      persistedState={addSourceState}
      onStateChange={persistAddSource}
      onJobStarted={handleJobStarted}
    />
  );

  const autoComparedKey = useRef("");
  useEffect(() => {
    if (!preferredSelection || !baseSnapshotId || !headSnapshotId) return;
    if (baseSnapshotId === headSnapshotId) return;
    const key = `${preferredSelection.headSha || preferredSelection.headRepoId}:${baseSnapshotId}:${headSnapshotId}`;
    if (autoComparedKey.current === key) return;
    autoComparedKey.current = key;
    handleCompare();
    // Run once after a completed add selects both sides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredSelection, baseSnapshotId, headSnapshotId]);

  const sourceList = (
    <div className="source-compare-sources" data-testid="source-compare-source-list">
      <h3>Available sources</h3>
      <p className="source-compare-muted">
        Every analyzed branch stays here. Open one to inspect files, or use two ready sources
        to compare.
      </p>
      {sourceItems.length === 0 ? (
        <p className="source-compare-muted" role="status">
          {loading
            ? "Loading sources…"
            : "No sources yet. Analyze this repo first, or add a branch or pull request."}
        </p>
      ) : (
        <ul className="source-compare-source-list">
          {sourceItems.map((item) => {
            const live = item.repoId ? liveByRepoId[String(item.repoId)] : null;
            const status = effectiveSourceStatus(item, live);
            const progress = live ? analysisProgressFromRepo(live) : null;
            const health = analysisHealthCopy(live);
            const stage = deriveAnalysisStage({
              status: live?.status || status,
              finalizeStatus: live?.finalizeStatus,
              progress,
            });
            const stageLabel = formatAnalysisStageLabel(stage);
            const analyzing =
              status === "processing" ||
              status === "queued" ||
              status === "pending";
            const failed = status === "failed";
            const canDeleteFailed =
              failed &&
              item.repoId &&
              String(item.repoId) !== String(canonicalRepoId || repoId);
            return (
            <li
              key={item.key}
              className={`source-compare-source-card${
                item.snapshotId && (item.snapshotId === baseSnapshotId || item.snapshotId === headSnapshotId)
                  ? " is-selected"
                  : ""
              }`}
            >
              <div>
                <strong>{item.title}</strong>
                <span className={`source-compare-source-status is-${status}`}>
                  {analyzing || failed ? stageLabel : formatSourceStatus(status)}
                </span>
                <p>
                  {analyzing || failed
                    ? [item.commitSha ? String(item.commitSha).slice(0, 7) : null, stageLabel]
                        .filter(Boolean)
                        .join(" · ")
                    : item.detail}
                </p>
                {item.snapshotId === baseSnapshotId && (
                  <p className="source-compare-source-role">Selected as left source</p>
                )}
                {item.snapshotId === headSnapshotId && (
                  <p className="source-compare-source-role">Selected as right source</p>
                )}
                {(analyzing || failed) && (
                  <div
                    className="source-compare-source-progress"
                    data-testid={`source-compare-live-progress-${item.repoId}`}
                  >
                    <p>
                      {failed
                        ? "This analysis did not finish."
                        : progress
                          ? formatAnalysisProgressCopy(progress, { stage })
                          : "Fetching the file list for this branch…"}
                    </p>
                    {analyzing && progress?.total > 0 && (
                      <ProgressBar
                        processed={progress.processedCount}
                        total={progress.total}
                        compact
                        stage={stage}
                      />
                    )}
                    {progress?.remainingPreview?.length > 0 && (
                      <>
                        <h4>Still to analyze</h4>
                        <ul>
                          {progress.remainingPreview.map((path) => (
                            <li key={path}>{path}</li>
                          ))}
                        </ul>
                        {progress.remainingOverflow > 0 && (
                          <p className="source-compare-muted">
                            +{progress.remainingOverflow} more
                          </p>
                        )}
                      </>
                    )}
                    {progress?.donePreview?.length > 0 && (
                      <>
                        <h4>Files analyzed</h4>
                        <ul>
                          {progress.donePreview.map((path) => (
                            <li key={path}>{path}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {health && (
                      <p className="source-compare-source-health" role="status">
                        {health}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="source-compare-source-actions">
                {item.comparable && (
                  <>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        setBaseSnapshotId(item.snapshotId);
                        setCompareResult(null);
                      }}
                    >
                      Use as left source
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        setHeadSnapshotId(item.snapshotId);
                        setCompareResult(null);
                      }}
                    >
                      Use as right source
                    </button>
                  </>
                )}
                {item.repoId && onOpenSource && (status === "ready" || status === "completed" || analyzing) && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() =>
                      onOpenSource({
                        repoId: item.repoId,
                        title: item.title,
                        status,
                      })
                    }
                  >
                    Open this analysis
                  </button>
                )}
                {canDeleteFailed && (
                  <button
                    type="button"
                    className="secondary-button source-compare-delete-failed"
                    onClick={() => handleDeleteFailedSource(item)}
                  >
                    Delete failed analysis
                  </button>
                )}
                {analyzing && !progress && (
                  <p className="source-compare-muted">
                    Analyzing this branch. Progress updates here every few seconds.
                  </p>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <section className="source-compare-panel" aria-label="Source Compare">
      <div className="source-compare-header">
        <h2>Source Compare</h2>
        <p className="source-compare-muted">
          Compare two analyzed sources for this repo. Add a branch or pull request, then pick it
          from the source list. Leaving this tab keeps the review and comparison here.
        </p>
        {addSource}
      </div>
      {sourceList}

      {legacyWarning && (
        <div className="source-compare-warning" role="status">
          Some older snapshots do not include source metadata. Re-analyze this repo to enable Source
          Compare by branch/ref/commit. Legacy snapshots remain selectable by analysis run.
        </div>
      )}

      {error && (
        <div className="source-compare-error" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {snapshots.length >= 2 ? (
      <div className="source-compare-controls">
        <div className="source-compare-field">
          <label htmlFor="source-compare-base">Left source (starting point)</label>
          <select
            id="source-compare-base"
            value={baseSnapshotId}
            onChange={(event) => {
              setBaseSnapshotId(event.target.value);
              setCompareResult(null);
            }}
          >
            {snapshots.map((snapshot) => (
              <option key={snapshot.id} value={snapshot.id}>
                {formatSnapshotSourceLabel(snapshot)}
              </option>
            ))}
          </select>
        </div>
        <div className="source-compare-field">
          <label htmlFor="source-compare-head">Right source (the other branch or PR)</label>
          <select
            id="source-compare-head"
            value={headSnapshotId}
            onChange={(event) => {
              setHeadSnapshotId(event.target.value);
              setCompareResult(null);
            }}
          >
            {snapshots.map((snapshot) => (
              <option key={snapshot.id} value={snapshot.id}>
                {formatSnapshotSourceLabel(snapshot)}
              </option>
            ))}
          </select>
        </div>
      </div>
      ) : (
        <p className="source-compare-muted" role="status">
          {sourceItems.some((item) => {
            const live = item.repoId ? liveByRepoId[String(item.repoId)] : null;
            const status = effectiveSourceStatus(item, live);
            return status === "processing" || status === "queued" || status === "pending";
          })
            ? "The other branch is still analyzing. Open the finished analysis above, or wait here for remaining files."
            : sourceItems.some((item) => {
                const live = item.repoId ? liveByRepoId[String(item.repoId)] : null;
                return effectiveSourceStatus(item, live) === "failed";
              })
              ? "A branch analysis failed. Delete it here, then add the branch again. Credits for the failed run are returned."
            : snapshots.length === 1
              ? "Add or finish the other branch to compare. Both sources stay in the list above."
              : loading
                ? "Loading snapshots…"
                : "Add a second branch or pull request to compare. You can still open the finished analysis above."}
        </p>
      )}

      {snapshots.length >= 2 && (
      <div className="source-compare-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleCompare}
          disabled={!canCompare}
        >
          <GitCompare size={16} aria-hidden="true" />
          Compare sources
        </button>
        {comparing && (
          <span className="source-compare-status" role="status">
            Comparing…
          </span>
        )}
      </div>
      )}

      {compareResult && (
        <div className="source-compare-results">
          {interpretation && (
            <div className="source-compare-interpretation-card">
              <h3>What changed</h3>
              <p className="source-compare-interpretation-summary">{interpretation.summary}</p>
              <div className="source-compare-interpretation-meta">
                <span>Change type: {interpretation.changeType}</span>
                <span>Scope: {interpretation.scope}</span>
                <span>Risk movement: {interpretation.riskMovement}</span>
                <span>Review priority: {interpretation.reviewPriority}</span>
                <span>Review first: {interpretation.reviewFirst.join(", ")}</span>
              </div>
            </div>
          )}

          <div className="source-compare-summary-card">
            <h3>Summary</h3>
            {headline && (
              <>
                <p>{headline.title}</p>
                <p>{headline.filesLine}</p>
                <p>{headline.mapsLine}</p>
                <p>{headline.riskLine}</p>
              </>
            )}
          </div>

          <div className="source-compare-meta-grid">
            <div className="source-compare-meta-card">
              <h3>Left source</h3>
              <h4>{formatSnapshotSourceLabel(compareResult.beforeSnapshot).split(" · ")[0]}</h4>
              {formatSourceMetadataLines(compareResult.beforeSnapshot).map((line) => (
                <p key={`base-${line.label}`} className="source-compare-meta-line">
                  <span>{line.label}: </span>
                  {line.value}
                </p>
              ))}
            </div>
            <div className="source-compare-meta-card">
              <h3>Right source</h3>
              <h4>{formatSnapshotSourceLabel(compareResult.afterSnapshot).split(" · ")[0]}</h4>
              {formatSourceMetadataLines(compareResult.afterSnapshot).map((line) => (
                <p key={`head-${line.label}`} className="source-compare-meta-line">
                  <span>{line.label}: </span>
                  {line.value}
                </p>
              ))}
            </div>
          </div>

          {comparisonSections.map((section) => (
            <div key={section.id} className="source-compare-section-card">
              <h3>{section.title}</h3>
              <ul className="source-compare-section-lines">
                {section.lines.map((line, index) => (
                  <li key={`${section.id}-line-${index}`}>{line}</li>
                ))}
              </ul>
              {section.details?.map((detail) => (
                <div key={`${section.id}-${detail.label}`}>
                  {detail.added?.length > 0 && (
                    <>
                      <p>
                        <strong>{detail.label} added</strong>
                      </p>
                      <ul className="source-compare-detail-list">
                        {detail.added.map((item) => (
                          <li key={`${detail.label}-added-${item}`}>{item}</li>
                        ))}
                      </ul>
                      {detail.addedRemaining > 0 && (
                        <p className="source-compare-more">+{detail.addedRemaining} more</p>
                      )}
                    </>
                  )}
                  {detail.removed?.length > 0 && (
                    <>
                      <p>
                        <strong>{detail.label} removed</strong>
                      </p>
                      <ul className="source-compare-detail-list">
                        {detail.removed.map((item) => (
                          <li key={`${detail.label}-removed-${item}`}>{item}</li>
                        ))}
                      </ul>
                      {detail.removedRemaining > 0 && (
                        <p className="source-compare-more">+{detail.removedRemaining} more</p>
                      )}
                    </>
                  )}
                  {detail.items?.length > 0 && (
                    <>
                      <p>
                        <strong>{detail.label}</strong>
                      </p>
                      <ul className="source-compare-detail-list">
                        {detail.items.map((item) => (
                          <li key={`${detail.label}-${item}`}>{item}</li>
                        ))}
                      </ul>
                      {detail.remaining > 0 && (
                        <p className="source-compare-more">+{detail.remaining} more</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default SourceComparePanel;
